// bluaIQ — Shared demo guard notice (PR #138A)
//
// Replaces real-action UI (upload, save, scan, billing, etc.) in demo
// projects with an honest bilingual "saved project required" card.
//
// Demo projects are FE-only constants (see src/utils/demoData.ts) with
// no backing database row. Any real mutation against them would 404 or
// hit Stripe / AI providers against a non-existent context. This card
// mirrors the established PR #137 QuotesModule pattern so the user
// sees a single consistent story across modules.

import { useTranslation } from 'react-i18next'
import { Lock } from 'lucide-react'

interface SavedProjectOnlyNoticeProps {
  /** Optional test id so module-specific smoke tests can pin the right card. */
  testId?: string
  /** Optional override copy (e.g. "Quote upload" vs "Photo upload"). */
  bodyEn?: string
  bodyEs?: string
}

export default function SavedProjectOnlyNotice({
  testId = 'saved-project-only-notice',
  bodyEn,
  bodyEs,
}: SavedProjectOnlyNoticeProps) {
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'

  const title = isEs ? 'Se requiere un proyecto guardado' : 'Saved project required'
  const body = isEs
    ? (bodyEs ?? 'Esta función está disponible en proyectos guardados. Crea o abre un proyecto real para usarla.')
    : (bodyEn ?? 'This feature is available in saved projects. Create or open a real project to use it.')

  return (
    <div
      className="border-2 border-dashed border-[#ECEAE4] rounded-xl p-6 text-center bg-[#FAFAF8]"
      data-testid={testId}
    >
      <Lock size={28} className="text-[#9E9890] mx-auto mb-2" />
      <p className="text-[12px] font-[800] text-[#1C1A17] mb-1">{title}</p>
      <p className="text-[11px] font-[600] text-[#6E6860] max-w-md mx-auto leading-snug">
        {body}
      </p>
    </div>
  )
}
